import type { ReactNode } from 'react'

import { Header, Icon, SubText, WithTooltip, Space } from '@procurify/ui'
import { useIntl } from 'react-intl'

import { FormattedShortDate } from '@webapp/core/components/FormattedDate/FormattedShortDate'
import { type TimezonesEnum } from '@webapp/core/entities/external/enums/TimezonesEnum'
import { useFormatBudgetPeriod } from '@webapp/hooks/useFormatBudgetPeriod'
import { type BudgetIntervalType } from '@webapp/models/enums/BudgetInterval'
import { BudgetCategoryCardHeaderStyled } from './styles/BudgetCategoryCardHeaderStyled'
import { BudgetNameWrapStyled } from './styles/BudgetNameWrapStyled'
import messages from '../../messages'
import { BudgetCategoryCardAttributesPopover } from '../BudgetCategoryCardAttributesPopover'
import { BudgetCategoryCardBudgetBreakdown } from '../BudgetCategoryCardBudgetBreakdown'
import { type BudgetBreakdownBar } from '../BudgetCategoryCardBudgetBreakdown/BudgetCategoryCardBudgetBreakdown'

export interface IBudgetCategoryCardHeaderProps {
  title?: string
  name: string
  nameTooltip?: string
  startDate?: Nullable<Date>
  endDate?: Nullable<Date>
  budgetBreakdown?: BudgetBreakdownBar[]
  timeZoneTooltip?: TimezonesEnum
  renderMenuButton?: () => ReactNode
  period?: Omit<BudgetIntervalType, 'none'>
  shouldShowBudgetPeriod?: boolean
  departments?: string[]
  accountCodes?: string[]
  isSourceOfTruth?: boolean
  budgetSelect?: ReactNode
}

export const BudgetCategoryCardHeader = ({
  title,
  name,
  nameTooltip,
  startDate,
  endDate,
  budgetBreakdown,
  timeZoneTooltip,
  shouldShowBudgetPeriod = false,
  period,
  renderMenuButton = () => null,
  departments,
  accountCodes,
  isSourceOfTruth,
  budgetSelect = null,
}: IBudgetCategoryCardHeaderProps) => {
  const intl = useIntl()
  const formatBudgetPeriodDate = useFormatBudgetPeriod()

  const showBudgetBreakdown = startDate && endDate && budgetBreakdown
  const showBudgetPeriod = startDate && endDate && !budgetSelect

  return (
    <BudgetCategoryCardHeaderStyled>
      <Space direction='vertical' size={'xsmall'}>
        <Space.Item>
          <Space
            size={'xsmall'}
            justify='space-between'
            align='start'
            direction='horizontal'
          >
            <Space.Item grow={1} shrink={1}>
              <Space direction='vertical' size={'small'}>
                {title ? (
                  <Space.Item>
                    <SubText bold autoMargin={false} color='textSecondary'>
                      {title}
                    </SubText>
                  </Space.Item>
                ) : null}

                <Space.Item>
                  <Space direction='horizontal' size={'xsmall'} justify='start'>
                    <Space.Item grow={1} shrink={1}>
                      <BudgetNameWrapStyled>
                        <Header as='h5'>{name}</Header>
                      </BudgetNameWrapStyled>
                    </Space.Item>
                    {Boolean(nameTooltip) && (
                      <WithTooltip title={nameTooltip}>
                        <Icon
                          icon='question'
                          size={14}
                          color={'textPrimary'}
                          mt={0.1}
                        />
                      </WithTooltip>
                    )}
                    {(departments || accountCodes) && (
                      <BudgetCategoryCardAttributesPopover
                        departments={departments}
                        accountCodes={accountCodes}
                        isSourceOfTruth={isSourceOfTruth}
                      />
                    )}
                  </Space>
                </Space.Item>

                {budgetSelect && (
                  <Space.Item grow={1}>{budgetSelect}</Space.Item>
                )}
              </Space>
            </Space.Item>

            <Space.Item>{renderMenuButton()}</Space.Item>
          </Space>
        </Space.Item>

        {showBudgetBreakdown && (
          <Space.Item>
            <BudgetCategoryCardBudgetBreakdown bars={budgetBreakdown} />
          </Space.Item>
        )}

        {showBudgetPeriod && (
          <Space.Item>
            <SubText bold autoMargin={false} color='textSecondary'>
              {shouldShowBudgetPeriod ? (
                formatBudgetPeriodDate(
                  startDate,
                  endDate,
                  period,
                  timeZoneTooltip
                )
              ) : (
                <>
                  <FormattedShortDate date={startDate} />
                  {' - '}
                  <FormattedShortDate date={endDate} />
                </>
              )}
              {timeZoneTooltip && (
                <WithTooltip
                  title={intl.formatMessage(messages.timeZoneTooltip, {
                    timeZone: timeZoneTooltip,
                  })}
                >
                  <Icon
                    ariaLabel={intl.formatMessage(
                      messages.dateRangeTooltipIconAriaLabel
                    )}
                    icon='question'
                    ml={0.25}
                  />
                </WithTooltip>
              )}
            </SubText>
          </Space.Item>
        )}
      </Space>
    </BudgetCategoryCardHeaderStyled>
  )
}

BudgetCategoryCardHeader.displayName = 'BudgetCategoryCardHeader'
